/**
 * Core Chess Game Types
 * 
 * This file defines all TypeScript types and interfaces for the chess game implementation.
 * It follows the design specified in .kiro/specs/chess-game/design.md
 */

// ============================================================================
// Basic Chess Types
// ============================================================================

/**
 * Position on the chess board
 * file: 0-7 representing columns a-h
 * rank: 0-7 representing rows 1-8
 */
export interface Position {
  file: number; // 0-7 (a-h)
  rank: number; // 0-7 (1-8)
}

/**
 * Chess piece types
 */
export type PieceType = 'king' | 'queen' | 'rook' | 'bishop' | 'knight' | 'pawn';

/**
 * Player colors
 */
export type PlayerColor = 'white' | 'black';

/**
 * Chess piece representation
 */
export interface ChessPiece {
  type: PieceType;
  color: PlayerColor;
  position: Position;
  hasMoved: boolean; // For castling and pawn double-move tracking
  moveCount: number;
}

/**
 * Chess board representation (8x8 grid)
 * board[rank][file] where rank=0-7, file=0-7
 * board[0][0] = a1 (white's queenside rook starting position)
 * board[7][7] = h8 (black's kingside rook starting position)
 */
export type Board = (ChessPiece | null)[][];

// ============================================================================
// Move Types
// ============================================================================

/**
 * Chess move representation
 */
export interface Move {
  from: Position;
  to: Position;
  piece: ChessPiece;
  captured?: ChessPiece;
  promotion?: PieceType;
  isCheck: boolean;
  isCheckmate: boolean;
  isCastling: boolean;
  isEnPassant: boolean;
  notation: string; // Algebraic notation
  timestamp: number;
}

/**
 * Result of a move attempt
 */
export interface MoveResult {
  success: boolean;
  move?: Move;
  error?: string;
  gameStatus: GameStatus;
}

// ============================================================================
// Game State Types
// ============================================================================

/**
 * Castling rights for both players
 */
export interface CastlingRights {
  whiteKingside: boolean;
  whiteQueenside: boolean;
  blackKingside: boolean;
  blackQueenside: boolean;
}

/**
 * Game status types
 */
export type GameStatus = 
  | { type: 'active' }
  | { type: 'check', player: PlayerColor }
  | { type: 'checkmate', winner: PlayerColor }
  | { type: 'stalemate' }
  | { type: 'draw', reason: 'agreement' | 'fifty-move' | 'repetition' | 'insufficient-material' }
  | { type: 'resignation', winner: PlayerColor }
  | { type: 'timeout', winner: PlayerColor };

/**
 * Complete game state
 */
export interface GameState {
  board: Board;
  currentPlayer: PlayerColor;
  moveHistory: Move[];
  capturedPieces: { white: ChessPiece[], black: ChessPiece[] };
  castlingRights: CastlingRights;
  enPassantSquare?: Position;
  halfMoveClock: number; // For fifty-move rule
  fullMoveNumber: number;
  gameStatus: GameStatus;
  positionHistory: string[]; // FEN strings for threefold repetition
}

// ============================================================================
// AI Types
// ============================================================================

/**
 * AI difficulty levels
 */
export type AIDifficulty = 'beginner' | 'intermediate' | 'expert';

/**
 * AI evaluation result
 */
export interface AIEvaluation {
  score: number;
  move: Move | null;
  depth: number;
}

// ============================================================================
// Multiplayer Types
// ============================================================================

/**
 * Game mode types
 */
export type GameMode = 'singleplayer' | 'multiplayer-realtime' | 'multiplayer-turnbased';

/**
 * Time control configuration
 */
export interface TimeControl {
  type: 'blitz' | 'rapid' | 'unlimited';
  initialTime: number; // seconds
  increment: number; // seconds per move
}

/**
 * Game settings
 */
export interface GameSettings {
  timeControl: TimeControl;
  mode: GameMode;
  color?: PlayerColor | 'random';
  isPrivate: boolean;
  aiDifficulty?: AIDifficulty; // For single-player mode
}

/**
 * Player information
 */
export interface Player {
  id: string;
  name: string;
  avatar?: string;
  color: PlayerColor;
}

/**
 * Game session
 */
export interface GameSession {
  gameId: string;
  players: {
    white: Player;
    black: Player;
  };
  settings: GameSettings;
  startTime: number;
  currentState: GameState;
}

/**
 * Room code for private games
 */
export interface RoomCode {
  code: string;
  gameId: string;
  expiresAt: number;
  creatorId: string;
}

// ============================================================================
// Notation Types
// ============================================================================

/**
 * Board state from FEN notation
 */
export interface BoardState {
  board: Board;
  currentPlayer: PlayerColor;
  castlingRights: CastlingRights;
  enPassantSquare?: Position;
  halfMoveClock: number;
  fullMoveNumber: number;
}

/**
 * Game metadata for PGN export
 */
export interface GameMetadata {
  event?: string;
  site?: string;
  date?: string;
  round?: string;
  white: string;
  black: string;
  result: string;
  timeControl?: string;
}

// ============================================================================
// Statistics Types
// ============================================================================

/**
 * Game statistics for a specific category
 */
export interface GameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageGameLength: number; // moves
}

/**
 * Complete chess statistics
 */
export interface ChessStatistics {
  userId?: string;
  singlePlayer: {
    beginner: GameStats;
    intermediate: GameStats;
    expert: GameStats;
  };
  multiplayer: {
    realtime: GameStats;
    turnbased: GameStats;
  };
  totalGames: number;
  totalPlayTime: number; // seconds
  longestGame: number; // moves
  shortestWin: number; // moves
  favoriteOpening?: string;
}

// ============================================================================
// Game Result Types
// ============================================================================

/**
 * Game result for statistics tracking
 */
export interface ChessGameResult {
  gameId: string;
  mode: GameMode;
  difficulty?: AIDifficulty;
  winner?: PlayerColor;
  isDraw: boolean;
  reason: string;
  moveCount: number;
  duration: number; // seconds
  completedAt: number;
}

// ============================================================================
// WebSocket Message Types
// ============================================================================

/**
 * WebSocket message types for multiplayer
 */
export type ChessWebSocketMessage =
  | { type: 'move', gameId: string, move: { from: Position, to: Position, promotion?: PieceType }, timestamp: number, clockTimes: { white: number, black: number } }
  | { type: 'draw_offer', gameId: string, fromPlayer: PlayerColor }
  | { type: 'draw_response', gameId: string, accepted: boolean }
  | { type: 'resign', gameId: string, player: PlayerColor }
  | { type: 'sync_request', gameId: string }
  | { type: 'sync_response', gameId: string, gameState: GameState };

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Helper type for position equality checks
 */
export function positionsEqual(pos1: Position, pos2: Position): boolean {
  return pos1.file === pos2.file && pos1.rank === pos2.rank;
}

/**
 * Helper type for position validation
 */
export function isValidPosition(pos: Position): boolean {
  return pos.file >= 0 && pos.file <= 7 && pos.rank >= 0 && pos.rank <= 7;
}

/**
 * Helper to convert position to algebraic notation
 */
export function positionToAlgebraic(pos: Position): string {
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  return `${files[pos.file]}${pos.rank + 1}`;
}

/**
 * Helper to convert algebraic notation to position
 */
export function algebraicToPosition(algebraic: string): Position | null {
  if (algebraic.length !== 2) return null;
  
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const file = files.indexOf(algebraic[0].toLowerCase());
  const rank = parseInt(algebraic[1]) - 1;
  
  // Check if file is valid and rank is a valid number
  if (file === -1 || isNaN(rank) || rank < 0 || rank > 7) return null;
  
  return { file, rank };
}
